<template>
  <section class="login-page min-h-screen flex items-center justify-center px-4 py-12 relative overflow-hidden">
    <!-- Background -->
    <div class="absolute inset-0 bg-neutral-950"></div>
    <div class="login-bg-glow"></div>

    <div class="w-full max-w-sm relative z-10">
      <!-- Branding -->
      <div class="text-center mb-10 login-entrance" style="animation-delay: 0s">
        <div class="flex justify-center mb-5">
          <div class="w-14 h-14 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/10 flex items-center justify-center">
            <img src="@/assets/globe.png" class="h-9 w-9" alt="Neutral Fuels" />
          </div>
        </div>
        <div class="flex items-center justify-center gap-2 mb-2">
          <h1 class="text-xl font-bold text-white tracking-tight">Neutral Fuels</h1>
          <span class="text-xs font-bold text-primary-400 px-1.5 py-0.5 rounded bg-primary-400/10 border border-primary-400/20">OMS</span>
        </div>
        <p class="text-sm text-neutral-400">Sign in to your account</p>
      </div>

      <!-- Card -->
      <div class="login-entrance bg-neutral-900 border border-neutral-800 rounded-2xl p-7 shadow-2xl" style="animation-delay: 0.1s">
        <form @submit.prevent="handleSubmit" class="space-y-5">
          <div>
            <label for="email" class="block text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-2">Email</label>
            <input
              id="email"
              v-model="email"
              type="email"
              autocomplete="email"
              placeholder="you@neutralfuels.com"
              required
              autofocus
              class="w-full px-4 py-2.5 bg-neutral-800 border border-neutral-700 rounded-xl text-base text-white placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500/50 transition-all duration-200"
            />
          </div>

          <div>
            <div class="flex items-center justify-between mb-2">
              <label for="password" class="block text-xs font-semibold text-neutral-400 uppercase tracking-wider">Password</label>
              <button
                type="button"
                @click="handleForgotPassword"
                class="text-xs text-primary-400 hover:text-primary-300 font-semibold transition-colors"
              >
                Forgot password?
              </button>
            </div>
            <div class="relative">
              <input
                id="password"
                v-model="password"
                :type="showPassword ? 'text' : 'password'"
                autocomplete="current-password"
                placeholder="Enter your password"
                required
                class="w-full px-4 py-2.5 pr-11 bg-neutral-800 border border-neutral-700 rounded-xl text-base text-white placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500/50 transition-all duration-200"
              />
              <button
                type="button"
                @click="showPassword = !showPassword"
                :aria-label="showPassword ? 'Hide password' : 'Show password'"
                class="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 text-neutral-500 hover:text-neutral-300 transition-colors"
              >
                <span class="material-icons text-[20px]" aria-hidden="true">{{ showPassword ? 'visibility_off' : 'visibility' }}</span>
              </button>
            </div>
          </div>

          <!-- Reset link sent -->
          <div v-if="resetSent" role="status" class="flex items-center gap-2 px-3 py-2.5 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
            <span class="material-icons text-emerald-400 text-sm" aria-hidden="true">check_circle</span>
            <p class="text-xs text-emerald-400">Reset link sent to {{ email }}. Check your inbox.</p>
          </div>

          <!-- Error -->
          <div v-if="error" role="alert" class="flex items-center gap-2 px-3 py-2.5 bg-red-500/10 border border-red-500/20 rounded-xl">
            <span class="material-icons text-red-400 text-sm" aria-hidden="true">error</span>
            <p class="text-xs text-red-400">{{ error }}</p>
          </div>

          <button
            type="submit"
            :disabled="isPending"
            class="w-full px-4 py-2.5 bg-primary-700 text-white rounded-xl hover:bg-primary-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-neutral-900 transition-all duration-200 font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <svg v-if="isPending" class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            {{ isPending ? 'Signing in...' : 'Sign In' }}
          </button>
        </form>

        <div class="mt-5 pt-5 border-t border-neutral-800 text-center">
          <p class="text-xs text-neutral-400">
            Don't have an account?
            <router-link :to="{ name: 'Signup', query: $route.query }" class="text-primary-400 hover:text-primary-300 font-semibold transition-colors ml-1">
              Sign up
            </router-link>
          </p>
        </div>
      </div>

      <!-- Footer -->
      <p class="login-entrance text-center text-[11px] text-neutral-500 mt-8" style="animation-delay: 0.2s">
        &copy; {{ currentYear }} Neutral Fuels Holdings Ltd.
      </p>
    </div>
  </section>
</template>

<script>
import { ref, computed } from '@vue/reactivity'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { sendPasswordResetEmail } from 'firebase/auth'
import { auth } from '@/firebase/config'

// Map Firebase auth error codes to human messages — raw err.message strings
// like "Firebase: Error (auth/invalid-credential)." must not reach users.
const AUTH_ERROR_MESSAGES = {
  'auth/invalid-credential': 'Incorrect email or password.',
  'auth/wrong-password': 'Incorrect email or password.',
  'auth/user-not-found': 'Incorrect email or password.',
  'auth/invalid-email': 'Please enter a valid email address.',
  'auth/user-disabled': 'This account has been disabled. Contact your administrator.',
  'auth/too-many-requests': 'Too many attempts — please try again in a few minutes.',
  'auth/network-request-failed': 'Network error — check your connection and try again.',
}

const friendlyAuthError = (err) =>
  AUTH_ERROR_MESSAGES[err?.code] || 'Something went wrong — please try again.'

export default {
  setup() {
    const email = ref('')
    const password = ref('')
    const error = ref(null)
    const isPending = ref(false)
    const showPassword = ref(false)
    const resetSent = ref(false)

    const router = useRouter()
    const store = useStore()

    const currentYear = computed(() => new Date().getFullYear())

    const handleSubmit = async () => {
      try {
        isPending.value = true
        error.value = null
        resetSent.value = false
        await store.dispatch('login', {
          email: email.value,
          password: password.value
        })
        const redirectPath = router.currentRoute.value.query.redirect || '/dashboard/operations/orders'
        router.push(redirectPath)
      } catch (err) {
        isPending.value = false
        error.value = friendlyAuthError(err)
      }
    }

    const handleForgotPassword = async () => {
      error.value = null
      resetSent.value = false
      if (!email.value) {
        error.value = 'Enter your email above, then click "Forgot password?".'
        return
      }
      try {
        await sendPasswordResetEmail(auth, email.value)
        resetSent.value = true
      } catch (err) {
        error.value = friendlyAuthError(err)
      }
    }

    return {
      email, password, handleSubmit, error, isPending, currentYear,
      showPassword, resetSent, handleForgotPassword,
    }
  }
}
</script>

<style scoped>
.login-bg-glow {
  position: absolute;
  top: -40%;
  left: 50%;
  transform: translateX(-50%);
  width: 600px;
  height: 600px;
  background: radial-gradient(circle, rgba(107, 142, 35, 0.12) 0%, transparent 70%);
  pointer-events: none;
}

.login-entrance {
  animation: loginFade 0.5s cubic-bezier(0.4, 0, 0.2, 1) both;
}

@media (prefers-reduced-motion: reduce) {
  .login-entrance {
    animation: none;
  }
}

@keyframes loginFade {
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
